import React, { useState, useMemo } from 'react';
import { Edit2, Calendar, CalendarCheck, CalendarX, CalendarClock, LayoutGrid, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import { useFetch } from '../hooks';
import { appointmentService, doctorService, patientService } from '../services/api';
import { getStatusColor, formatDate, formatCurrency } from '../utils/helpers';

const APPT_STATUSES = ['pending', 'accepted', 'rejected', 'cancelled'];

const today     = new Date().toISOString().split('T')[0];
const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
const tomorrow  = new Date(Date.now() + 86400000).toISOString().split('T')[0];

/* ── Quick date filter chips ─────────────────────────────────────────────── */
const DATE_CHIPS = [
  { key: 'all',       label: 'All'       },
  { key: 'today',     label: 'Today'     },
  { key: 'tomorrow',  label: 'Tomorrow'  },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'upcoming',  label: 'Upcoming'  },
  { key: 'past',      label: 'Past'      },
  { key: 'custom',    label: 'Pick Date' },
];

/* ── Stat Card ───────────────────────────────────────────────────────────── */
const StatCard = ({ icon: Icon, label, value, color, bg, onClick, active }) => (
  <button
    onClick={onClick}
    className={`card p-4 flex items-center gap-4 w-full text-left transition-all hover:shadow-md ${active ? 'ring-2 ring-medihub-primary dark:ring-medihub-highlight' : ''}`}
  >
    <div className={`p-3 rounded-xl shrink-0 ${bg}`}>
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  </button>
);

/* ── Page ────────────────────────────────────────────────────────────────── */
const Appointments = () => {
  const { data: appointments, loading, refetch } = useFetch(() => appointmentService.getAll());
  const { data: doctors }   = useFetch(() => doctorService.getAll());
  const { data: patients }  = useFetch(() => patientService.getAll());

  const [statusFilter, setStatusFilter] = useState('all');
  const [dateChip,     setDateChip]     = useState('all');
  const [customDate,   setCustomDate]   = useState('');
  const [selected,     setSelected]     = useState(null);
  const [modal,        setModal]        = useState(null);
  const [statusExtra, setStatusExtra] = useState({ video_call_link: '', rejection_reason: '', cancel_reason: '' });

  // Book form state
  const [bookForm, setBookForm] = useState({
    user_id: '', doctor_id: '', slot_id: '', appointment_date: '',
    consultancy_type: 'online', reason: '', clinic_note: '', reports: [],
  });
  const [bookLoading,    setBookLoading]    = useState(false);
  const [availability,   setAvailability]   = useState([]);
  const [slotsLoading,   setSlotsLoading]   = useState(false);
  const [showBook, setShowBook]=useState(false)

  const selectedBookDoctor = doctors?.find(d => d.id === parseInt(bookForm.doctor_id));

  // Flatten all available slots from availability
  const allSlots = availability.flatMap(day =>
    day.is_available ? day.slots.map(s => ({ ...s, day: day.day })) : []
  );

  const handleDoctorChange = async (doctorId) => {
    setBookForm(p => ({ ...p, doctor_id: doctorId, slot_id: '' }));
    setAvailability([]);
    if (!doctorId) return;
    setSlotsLoading(true);
    try {
      const avail = await doctorService.getAvailability(doctorId);
      setAvailability(avail);
    } catch { toast.error('Failed to load slots'); }
    finally { setSlotsLoading(false); }
  };

  /* ── Stats ── */
  const stats = useMemo(() => {
    const all = appointments || [];
    return {
      total:     all.length,
      requested: all.filter(a => a.status === 'pending').length,
      upcoming:  all.filter(a => a.appointment_date > today && a.status === 'accepted').length,
      completed:  all.filter(a => a.appointment_date < today && a.status === 'completed').length,
      cancelled: all.filter(a => a.status === 'cancelled' || a.status === 'rejected').length,
    };
  }, [appointments]);

  /* ── Filtering ── */
  const filtered = useMemo(() => {
    let list = appointments || [];
    if (statusFilter !== 'all') list = list.filter(a => a.status === statusFilter);
    switch (dateChip) {
      case "today":
        list = list.filter((a) => a.appointment_date === today);
        break;
      case "tomorrow":
        list = list.filter((a) => a.appointment_date === tomorrow);
        break;
      case "yesterday":
        list = list.filter((a) => a.appointment_date === yesterday);
        break;
      case "upcoming":
        list = list.filter(
          (a) => a.appointment_date > today && a.status === "accepted",
        );
        break;
      case "completed":
        list = list.filter((a) => a.appointment_date < today);
        break;
      case "past":
        list = list.filter((a) => a.appointment_date < today);
        break;
      case "custom":
        if (customDate)
          list = list.filter((a) => a.appointment_date === customDate);
        break;
      default:
        break;
    }
    return list;
  }, [appointments, statusFilter, dateChip, customDate]);

  /* ── Handlers ── */
  const handleBook = async () => {
    if (!bookForm.user_id || !bookForm.doctor_id || !bookForm.slot_id || !bookForm.appointment_date || !bookForm.consultancy_type) {
      toast.error('Please fill all required fields'); return;
    }
    setBookLoading(true);
    try {
      const res = await appointmentService.book(bookForm);
      toast.success(res?.message || 'Appointment booked!');
      setShowBook(false);
      setBookForm({ user_id: '', doctor_id: '', slot_id: '', appointment_date: '', consultancy_type: 'online', reason: '', clinic_note: '', reports: [] });
      refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to book'); }
    finally { setBookLoading(false); }
  };


  const handleStatusChange = async (newStatus) => {
    const payload = { status: newStatus };
    if (newStatus === 'accepted' && selected?.consultancy_type === 'online') payload.video_call_link = statusExtra.video_call_link;
    if (newStatus === 'rejected')  payload.rejection_reason = statusExtra.rejection_reason;
    if (newStatus === 'cancelled') payload.cancel_reason    = statusExtra.cancel_reason;
    try {
      const res = await appointmentService.updateStatus(selected.id, payload);
      toast.success(res?.message || `Status updated to ${newStatus}`);
      refetch();
      setModal(null);
      setStatusExtra({ video_call_link: '', rejection_reason: '', cancel_reason: '' });
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to update status'); }
  };

  /* ── Navigate custom date ── */
  const shiftDate = (dir) => {
    const base = customDate || today;
    const d = new Date(base);
    d.setDate(d.getDate() + dir);
    setCustomDate(d.toISOString().split('T')[0]);
    setDateChip('custom');
  };

  /* ── Columns ── */
  const columns = [
    { key: 'id', label: '#', render: v => <span className="font-mono text-gray-500 text-xs">#{v}</span> },
    {
      key: 'patient_name', label: 'Patient', render: (v, row) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{v}</p>
          <p className="text-xs text-gray-500">{row.patient_mobile}</p>
        </div>
      )
    },
    {
      key: 'doctor_name', label: 'Doctor', render: (v, row) => (
        <div>
          <p className="text-sm text-gray-700 dark:text-gray-300">{v}</p>
          <p className="text-xs text-gray-500">{row.specialization}</p>
        </div>
      )
    },
    {
      key: 'appointment_date', label: 'Date & Time', render: (v, row) => (
        <div>
          <p className="text-sm text-gray-700 dark:text-gray-300">{formatDate(v)}</p>
          <p className="text-xs text-gray-500">{row.slot_start} - {row.slot_end}</p>
        </div>
      )
    },
    { key: 'consultancy_type', label: 'Type', render: v => <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">{v}</span> },
    { key: 'consultation_fee', label: 'Fee', render: v => <span className="font-semibold text-green-600 dark:text-green-400">{formatCurrency(v)}</span> },
    { key: 'reason', label: 'Reason', render: v => <span className="text-xs text-gray-500">{v || '—'}</span> },
    {
      key: 'status', label: 'Status', render: v => (
        <span className={`badge capitalize ${getStatusColor(v)}`}>{v}</span>
      )
    },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <button
          onClick={() => { setSelected(row); setModal('status'); }}
          className="p-1.5 hover:bg-medihub-light dark:hover:bg-medihub-dark/20 rounded-lg transition-colors text-medihub-primary"
          title="Change Status"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      )
    },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Appointments</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">View and manage all appointments</p>
        </div>
        <button onClick={() => setShowBook(true)} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Book Appointment
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={LayoutGrid}
          label="Total Appointments"
          value={stats.total}
          color="text-gray-900 dark:text-white"
          bg="bg-gray-100 dark:bg-gray-700"
          onClick={() => { setStatusFilter('all'); setDateChip('all'); }}
          active={statusFilter === 'all' && dateChip === 'all'}
        />
        <StatCard
          icon={CalendarClock}
          label="Pending"
          value={stats.requested}
          color="text-blue-600 dark:text-blue-400"
          bg="bg-blue-50 dark:bg-blue-900/20"
          onClick={() => { setStatusFilter('pending'); setDateChip('all'); }}
          active={statusFilter === 'pending'}
        />
        <StatCard
          icon={CalendarCheck}
          label="Upcoming"
          value={stats.upcoming}
          color="text-green-600 dark:text-green-400"
          bg="bg-green-50 dark:bg-green-900/20"
          onClick={() => { setStatusFilter('all'); setDateChip('upcoming'); }}
          active={dateChip === 'upcoming'}
        />
        <StatCard
          icon={CalendarCheck}
          label="Completed"
          value={stats.completed}
          color="text-green-600 dark:text-green-400"
          bg="bg-green-50 dark:bg-green-900/20"
          onClick={() => { setStatusFilter('all'); setDateChip('completed'); }}
          active={dateChip === 'completed'}
        />
        <StatCard
          icon={CalendarX}
          label="Cancelled"
          value={stats.cancelled}
          color="text-red-600 dark:text-red-400"
          bg="bg-red-50 dark:bg-red-900/20"
          onClick={() => { setStatusFilter('cancelled'); setDateChip('all'); }}
          active={statusFilter === 'cancelled'}
        />
      </div>

      {/* Filters Row */}
      <div className="card p-4 space-y-3">
        {/* Date quick chips */}
        <div className="flex flex-wrap items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
          {DATE_CHIPS.map(chip => (
            chip.key !== 'custom' ? (
              <button
                key={chip.key}
                onClick={() => { setDateChip(chip.key); if (chip.key !== 'custom') setCustomDate(''); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  dateChip === chip.key
                    ? 'bg-medihub-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {chip.label}
              </button>
            ) : (
              /* Custom date picker with prev/next arrows */
              <div key="custom" className="flex items-center gap-1">
                <button
                  onClick={() => shiftDate(-1)}
                  className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <input
                  type="date"
                  value={customDate}
                  onChange={e => { setCustomDate(e.target.value); setDateChip('custom'); }}
                  className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors outline-none ${
                    dateChip === 'custom'
                      ? 'border-medihub-primary bg-medihub-light dark:bg-medihub-dark/20 text-medihub-secondary dark:text-medihub-highlight'
                      : 'border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                />
                <button
                  onClick={() => shiftDate(1)}
                  className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )
          ))}
        </div>

        {/* Status chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-400 font-medium w-[68px] shrink-0">Status:</span>
          {['all', ...APPT_STATUSES].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                statusFilter === s
                  ? 'bg-medihub-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {s === 'all' ? 'All Status' : s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Table
        title={`Appointments (${filtered.length}${filtered.length !== (appointments?.length || 0) ? ` of ${appointments?.length || 0}` : ''})`}
        columns={columns}
        data={filtered}
        loading={loading}
        searchKeys={['patient', 'doctor', 'type']}
        exportFilename="appointments"
      />

      <Modal isOpen={modal === 'status'} onClose={() => { setModal(null); setStatusExtra({ video_call_link: '', rejection_reason: '', cancel_reason: '' }); }} title="Change Appointment Status" size="sm">
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-sm space-y-1">
            <p><span className="text-gray-500">Patient:</span> <span className="font-medium">{selected?.patient_name}</span></p>
            <p><span className="text-gray-500">Doctor:</span> <span className="font-medium">{selected?.doctor_name}</span></p>
            <p><span className="text-gray-500">Date:</span> <span className="font-medium">{selected?.appointment_date && formatDate(selected.appointment_date)} {selected?.slot_start && `at ${selected.slot_start}`}</span></p>
            <p><span className="text-gray-500">Current:</span> <span className={`badge ml-1 ${getStatusColor(selected?.status)}`}>{selected?.status}</span></p>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Set new status:</p>
            <div className="grid grid-cols-2 gap-2">
              {['accepted','rejected','completed','cancelled'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusExtra(p => ({ ...p, _pending: s }))}
                  className={`py-2 px-3 rounded-lg text-sm font-medium capitalize transition-colors ${
                    statusExtra._pending === s
                      ? 'bg-medihub-primary text-white'
                      : selected?.status === s
                      ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-default'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Conditional extra fields */}
          {statusExtra._pending === 'accepted' && selected?.consultancy_type === 'online' && (
            <FormField label="Video Call Link" required>
              <input
                value={statusExtra.video_call_link}
                onChange={e => setStatusExtra(p => ({ ...p, video_call_link: e.target.value }))}
                className="input-field" placeholder="https://zoom.us/..."
              />
            </FormField>
          )}
          {statusExtra._pending === 'rejected' && (
            <FormField label="Rejection Reason">
              <input
                value={statusExtra.rejection_reason}
                onChange={e => setStatusExtra(p => ({ ...p, rejection_reason: e.target.value }))}
                className="input-field" placeholder="Doctor unavailable"
              />
            </FormField>
          )}
          {statusExtra._pending === 'cancelled' && (
            <FormField label="Cancel Reason">
              <input
                value={statusExtra.cancel_reason}
                onChange={e => setStatusExtra(p => ({ ...p, cancel_reason: e.target.value }))}
                className="input-field" placeholder="Patient request"
              />
            </FormField>
          )}

          <div className="flex gap-2 pt-1">
            <button onClick={() => { setModal(null); setStatusExtra({ video_call_link: '', rejection_reason: '', cancel_reason: '' }); }} className="btn-secondary flex-1">Cancel</button>
            <button
              onClick={() => handleStatusChange(statusExtra._pending)}
              disabled={!statusExtra._pending}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      {/* Book Appointment Modal */}
      <Modal isOpen={showBook} onClose={() => setShowBook(false)} title="Book Appointment" size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Patient" required>
              <select value={bookForm.user_id} onChange={e => setBookForm(p => ({ ...p, user_id: e.target.value }))} className="input-field">
                <option value="">Select patient</option>
                {(patients || []).map(p => <option key={p.id} value={p.id}>{p.name} — {p.mobile}</option>)}
              </select>
            </FormField>
            <FormField label="Doctor" required>
              <select value={bookForm.doctor_id} onChange={e => handleDoctorChange(e.target.value)} className="input-field">
                <option value="">Select doctor</option>
                {(doctors || []).map(d => <option key={d.id} value={d.id}>{d.name} — {d.specialization}</option>)}
              </select>
            </FormField>
            {selectedBookDoctor && (
              <div className="sm:col-span-2 p-3 bg-medihub-light dark:bg-medihub-dark/20 rounded-xl flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Consultation Fee</span>
                <span className="font-bold text-medihub-primary dark:text-medihub-highlight text-lg">{formatCurrency(selectedBookDoctor.consultation_fee || selectedBookDoctor.fees || 0)}</span>
              </div>
            )}
            <FormField label="Slot" required>
              <select value={bookForm.slot_id} onChange={e => setBookForm(p => ({ ...p, slot_id: e.target.value }))} className="input-field" disabled={slotsLoading || !bookForm.doctor_id}>
                <option value="">{slotsLoading ? 'Loading slots...' : !bookForm.doctor_id ? 'Select doctor first' : allSlots.length === 0 ? 'No slots available' : 'Select slot'}</option>
                {allSlots.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.day.charAt(0).toUpperCase() + s.day.slice(1)} — {s.start_time.slice(0,5)} to {s.end_time.slice(0,5)}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Appointment Date" required>
              <input type="date" value={bookForm.appointment_date} min={today} onChange={e => setBookForm(p => ({ ...p, appointment_date: e.target.value }))} className="input-field" />
            </FormField>
            <FormField label="Consultancy Type" required>
              <select value={bookForm.consultancy_type} onChange={e => setBookForm(p => ({ ...p, consultancy_type: e.target.value }))} className="input-field">
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
            </FormField>
            <FormField label="Reason">
              <input value={bookForm.reason} onChange={e => setBookForm(p => ({ ...p, reason: e.target.value }))} className="input-field" placeholder="Reason for visit" />
            </FormField>
          </div>
          <FormField label="Clinic Note">
            <textarea value={bookForm.clinic_note} onChange={e => setBookForm(p => ({ ...p, clinic_note: e.target.value }))} className="input-field resize-none" rows={2} placeholder="Optional clinic note..." />
          </FormField>
          <FormField label="Reports (max 5 files)">
            <input type="file" accept="image/*,.pdf" multiple className="input-field"
              onChange={e => setBookForm(p => ({ ...p, reports: Array.from(e.target.files).slice(0, 5) }))} />
          </FormField>
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={() => setShowBook(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleBook} disabled={bookLoading} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              {bookLoading ? 'Booking...' : 'Book Appointment'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Appointments;
